/**
 * CatalogJob — GVW-124 Conversion-job progress
 * 
 * Per brief.md: Persistent banner anchored above the canvas or in notification inbox.
 * Shows conversion progress for NATS-driven jobs with "Open in GeoConverter" link.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { catalogClient, type UploadJob, type CatalogResult } from '../../catalog/catalogClient';

interface CatalogJobProps {
  className?: string;
}

interface CatalogJobState {
  job: UploadJob | null;
  loading: boolean;
  error: string | null;
}

export function CatalogJob({ className }: CatalogJobProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<CatalogJobState>({
    job: null,
    loading: true,
    error: null,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadJob = useCallback(async () => {
    if (!id) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'No job ID provided',
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    const result: CatalogResult<UploadJob> = await catalogClient.getJob(id);
    
    if (result.ok) {
      setState(prev => ({
        ...prev,
        job: result.data,
        loading: false,
      }));
    } else {
      let errorMessage = 'Failed to load job';
      if (result.reason === 'backend-not-deployed') {
        errorMessage = 'Backend not deployed. The job service is not available.';
      } else if (result.reason === 'not-found') {
        errorMessage = 'Job not found. It may have been completed or removed.';
      } else if (result.reason === 'unauthorized') {
        errorMessage = 'You do not have access to this job.';
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [id]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  useEffect(() => {
    // Set up polling for active jobs
    const pollInterval = setInterval(() => {
      const currentState = stateRef.current;
      if (currentState.job && ['waiting', 'validating', 'running'].includes(currentState.job.status)) {
        loadJob();
      }
    }, 2000); // Poll every 2 seconds
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [loadJob]);

  const getStatusIcon = (status: UploadJob['status']) => {
    switch (status) {
      case 'waiting': return 'schedule';
      case 'validating': return 'hourglass_empty';
      case 'ready': return 'check_circle';
      case 'running': return 'play_arrow';
      case 'needs-input': return 'info';
      case 'failed': return 'error';
      case 'completed': return 'check';
      case 'cancelled': return 'cancel';
      default: return 'help';
    }
  };

  const getBannerSeverity = (status: UploadJob['status']) => {
    switch (status) {
      case 'waiting':
      case 'validating':
      case 'running': return 'info';
      case 'ready':
      case 'completed': return 'success';
      case 'needs-input': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      default: return 'info';
    }
  };

  const getStatusMessage = (job: UploadJob) => {
    switch (job.status) {
      case 'waiting': return 'Conversion in queue';
      case 'validating': return 'Validating file format';
      case 'ready': return 'Ready for conversion';
      case 'running': return job.stage ? `Converting: ${job.stage}` : 'Converting';
      case 'needs-input': return 'Additional input required';
      case 'failed': return job.error || 'Conversion failed';
      case 'completed': return `${job.fileName} is ready. Add it from the catalog.`;
      case 'cancelled': return 'Conversion cancelled';
      default: return 'Unknown status';
    }
  };

  if (state.loading) {
    return (
      <div className={`catalog-job ${className || ''}`} aria-busy="true">
        <div className="catalog-job__loading">
          <div className="catalog-job__skeleton" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`catalog-job ${className || ''}`} role="alert">
        <div className="catalog-job__error">
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <h1>We could not load this job</h1>
          <p>{state.error}</p>
          <button onClick={loadJob} type="button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!state.job) {
    return (
      <div className={`catalog-job ${className || ''}`}>
        <div className="catalog-job__empty">
          <span className="material-symbols-outlined" aria-hidden="true">work</span>
          <h1>Job not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className={`catalog-job ${className || ''}`} data-testid="catalog-job">
      <div className={`catalog-job__banner catalog-job__banner--${getBannerSeverity(state.job.status)}`}>
        <div className="catalog-job__banner-content">
          <div className="catalog-job__banner-icon">
            <span className="material-symbols-outlined" aria-hidden="true">
              {getStatusIcon(state.job.status)}
            </span>
          </div>
          
          <div className="catalog-job__banner-info">
            <h1>Conversion Job</h1>
            <p>{getStatusMessage(state.job)}</p>
            
            {state.job.status === 'running' && typeof state.job.progress === 'number' && (
              <div className="catalog-job__progress">
                <div 
                  className="catalog-job__progress-bar"
                  style={{ width: `${state.job.progress}%` }}
                  role="progressbar"
                  aria-valuenow={state.job.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuetext={`${state.job.progress} percent${state.job.stage ? `: ${state.job.stage}` : ''}`}
                />
              </div>
            )}
            
            <div className="catalog-job__banner-chips">
              <span className="catalog-job__chip catalog-job__chip--type">
                <span className="material-symbols-outlined" aria-hidden="true">sync_alt</span>
                Converter
              </span>
              
              <span className={`catalog-job__chip catalog-job__chip--status catalog-job__chip--${getBannerSeverity(state.job.status)}`}>
                {state.job.status}
              </span>
            </div>
          </div>
        </div>
        
        <div className="catalog-job__banner-actions">
          {state.job.status === 'completed' && (
            <button
              type="button"
              className="catalog-job__action-button catalog-job__action-button--primary"
              onClick={() => {
                // TODO: Add to current view or navigate to catalog
                console.log('Add completed job to catalog:', state.job?.id);
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              Add
            </button>
          )}
          
          <button
            type="button"
            className="catalog-job__action-button"
            onClick={() => {
              // TODO: Open in GeoConverter (cross-app link)
              console.log('Open in GeoConverter:', state.job?.id);
              window.open(`/geoconverter/jobs/${state.job?.id}`, '_blank', 'noopener,noreferrer');
            }}
            aria-label="Open job details in GeoConverter"
          >
            <span className="material-symbols-outlined" aria-hidden="true">open_in_new</span>
            Open in GeoConverter
          </button>
          
          {['waiting', 'validating', 'running'].includes(state.job.status) && (
            <button
              type="button"
              className="catalog-job__action-button catalog-job__action-button--danger"
              onClick={() => {
                // TODO: Cancel job
                console.log('Cancel job:', state.job?.id);
              }}
              aria-label="Cancel conversion job"
            >
              <span className="material-symbols-outlined" aria-hidden="true">cancel</span>
              Cancel
            </button>
          )}
        </div>
      </div>
      
      {/* Detailed job information */}
      <div className="catalog-job__details">
        <h2>Job Details</h2>
        
        <div className="catalog-job__info-grid">
          <div className="catalog-job__info-item">
            <dt>File Name</dt>
            <dd>{state.job.fileName}</dd>
          </div>
          
          <div className="catalog-job__info-item">
            <dt>File Size</dt>
            <dd>{(state.job.fileSize / (1024 * 1024)).toFixed(2)} MB</dd>
          </div>
          
          <div className="catalog-job__info-item">
            <dt>Status</dt>
            <dd>
              <span className={`catalog-job__status-indicator catalog-job__status-indicator--${getBannerSeverity(state.job.status)}`}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  {getStatusIcon(state.job.status)}
                </span>
                {state.job.status}
              </span>
            </dd>
          </div>
          
          {state.job.progress !== undefined && (
            <div className="catalog-job__info-item">
              <dt>Progress</dt>
              <dd>{state.job.progress}%</dd>
            </div>
          )}
          
          {state.job.stage && (
            <div className="catalog-job__info-item">
              <dt>Current Stage</dt>
              <dd>{state.job.stage}</dd>
            </div>
          )}
          
          {state.job.error && (
            <div className="catalog-job__info-item">
              <dt>Error</dt>
              <dd className="catalog-job__error-message">{state.job.error}</dd>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}